import React, { useEffect, useRef, useState } from "react";
import { X, Check, Loader2, ZoomIn, ZoomOut, RotateCw } from "lucide-react";

/**
 * Modal that loads a user-supplied image, lets them pan/zoom inside a
 * circular preview, and then emits a SQUARE PNG Blob (with the circle area
 * filled and the outside transparent). Output is `outputSize × outputSize`.
 *
 * Props:
 *   file        : File           — the image picked by the user (required)
 *   open        : boolean        — show / hide
 *   outputSize  : number         — px (default 384) — square PNG dimension
 *   onCancel    : () => void
 *   onConfirm   : (blob, dataUrl) => void   — receives the cropped image
 */
export default function CircularImageCropper({
  file,
  open,
  outputSize = 384,
  onCancel,
  onConfirm,
}) {
  const stageRef = useRef(null);
  const imgRef = useRef(null);

  const [imgSrc, setImgSrc] = useState("");
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, ox: 0, oy: 0 });
  const [busy, setBusy] = useState(false);

  const STAGE = 320; // visible stage size in px

  // Load file as data URL
  useEffect(() => {
    if (!file || !open) { setImgSrc(""); return; }
    const reader = new FileReader();
    reader.onload = () => setImgSrc(String(reader.result || ""));
    reader.readAsDataURL(file);
  }, [file, open]);

  // Reset transforms when a new image loads
  useEffect(() => {
    if (!imgSrc) return;
    const img = new Image();
    img.onload = () => {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
      // Initial zoom: cover the circle (so the smaller dimension fills STAGE)
      const min = Math.min(img.naturalWidth, img.naturalHeight) || 1;
      const init = STAGE / min;
      setZoom(init);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
    };
    img.src = imgSrc;
  }, [imgSrc]);

  // Pointer drag handlers (work for both mouse and touch)
  const onPointerDown = (e) => {
    if (!imgSrc) return;
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    setOffset({ x: dragStart.ox + dx, y: dragStart.oy + dy });
  };
  const onPointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  // Mouse-wheel zoom — fine adjustment
  const onWheel = (e) => {
    if (!imgSrc) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setZoom((z) => clamp(z + z * delta, 0.1, 8));
  };

  const handleConfirm = async () => {
    if (!imgSrc || !imgSize.w) return;
    setBusy(true);
    try {
      const blob = await renderCroppedPng({
        imgSrc,
        imgSize,
        stage: STAGE,
        zoom,
        rotation,
        offset,
        outputSize,
      });
      const dataUrl = await blobToDataUrl(blob);
      onConfirm?.(blob, dataUrl);
    } catch (err) {
      console.error("[CircularImageCropper] render failed", err);
      alert("Şəkli kəsmək alınmadı: " + (err?.message || err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="circular-cropper"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div className="bg-white text-black w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-black/10">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/10">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-black/40">Şəkli Kəs</div>
            <h3 className="font-display text-xl">Dairəvi Kəsmə</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center text-black/60 hover:text-black"
            aria-label="Bağla"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stage */}
        <div className="p-5">
          <div className="relative mx-auto" style={{ width: STAGE, height: STAGE }}>
            {/* Image stage with overflow hidden so dragged image is clipped */}
            <div
              ref={stageRef}
              className="absolute inset-0 overflow-hidden bg-zinc-900 select-none touch-none"
              style={{ borderRadius: "50%" }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onWheel={onWheel}
            >
              {imgSrc && imgSize.w > 0 && (
                <img
                  ref={imgRef}
                  src={imgSrc}
                  alt=""
                  draggable={false}
                  className="absolute pointer-events-none"
                  style={{
                    left: "50%",
                    top: "50%",
                    width: imgSize.w,
                    height: imgSize.h,
                    transformOrigin: "center",
                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`,
                    transition: dragging ? "none" : "transform 60ms linear",
                  }}
                />
              )}
            </div>

            {/* Circle outline ring */}
            <div
              className="absolute inset-0 pointer-events-none ring-2 ring-white/90 shadow-[0_0_0_2000px_rgba(0,0,0,0.0)]"
              style={{ borderRadius: "50%" }}
            />
            {/* Grid lines (rule of thirds) */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ borderRadius: "50%", overflow: "hidden" }}>
              <div className="w-full h-px bg-white/15" />
            </div>
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ borderRadius: "50%", overflow: "hidden" }}>
              <div className="h-full w-px bg-white/15" />
            </div>
          </div>

          {/* Zoom slider */}
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setZoom((z) => clamp(z * 0.85, 0.1, 8))}
              className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:border-black"
              aria-label="Kiçilt"
            >
              <ZoomOut size={14} />
            </button>
            <input
              type="range"
              min={0.1}
              max={8}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-black"
              aria-label="Zoom"
            />
            <button
              type="button"
              onClick={() => setZoom((z) => clamp(z * 1.15, 0.1, 8))}
              className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:border-black"
              aria-label="Böyüt"
            >
              <ZoomIn size={14} />
            </button>
            <button
              type="button"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:border-black"
              aria-label="Fırlat"
              title="90° fırlat"
            >
              <RotateCw size={14} />
            </button>
          </div>

          <p className="mt-3 text-[11px] text-black/50 leading-relaxed text-center">
            Şəkli sürükləyin, slider ilə yaxınlaşdırın. Dairənin daxilində qalan
            hissə logo kimi göstəriləcək.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-3 border-t border-black/10 bg-zinc-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm text-black/70 hover:text-black"
          >
            Ləğv et
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !imgSrc}
            className="px-5 py-2 rounded-full bg-black text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            data-testid="cropper-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {busy ? "Hazırlanır…" : "Təsdiq et"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/**
 * Render the visible portion of the image (inside the on-screen circle)
 * into a square canvas of `outputSize × outputSize`. The circle area is
 * kept and the rest is transparent. Returns a PNG Blob.
 *
 * The on-screen transform pipeline is:
 *   translate(-50%, -50%) translate(offset) rotate(rotation) scale(zoom)
 * applied to a centered image inside a `stage × stage` container.
 * We replay the same pipeline on a canvas at the requested output size.
 */
function renderCroppedPng({ imgSrc, imgSize, stage, zoom, rotation, offset, outputSize }) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const out = outputSize;
      const scale = out / stage; // visible-stage → output px

      const canvas = document.createElement("canvas");
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext("2d");

      // Circular mask
      ctx.save();
      ctx.beginPath();
      ctx.arc(out / 2, out / 2, out / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Center, then apply user transforms (in the same order as the CSS pipeline)
      ctx.translate(out / 2, out / 2);
      ctx.translate(offset.x * scale, offset.y * scale);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom * scale, zoom * scale);

      // Draw image centered at (0,0)
      ctx.drawImage(img, -imgSize.w / 2, -imgSize.h / 2, imgSize.w, imgSize.h);

      ctx.restore();

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas blob yaradıla bilmədi"));
          resolve(blob);
        },
        "image/png",
        0.95
      );
    };
    img.onerror = () => reject(new Error("Şəkil yüklənmədi"));
    img.src = imgSrc;
  });
}
